import Link from 'next/link'
import AppImage from '@/components/AppImage'
import { isThisWeek, isThisMonth, isToday, isTomorrow } from 'date-fns'

import PostLabel from '@/components/ui/PostLabel'
import { decodeSafeEntities } from '@/lib/sanitise-strings'

import type { Hit } from 'instantsearch.js'

export type GridArchiveItemProps = {
  hit: Hit
  size?: 'l'
}

function getDateInWords(date: Date): string {
  if (isToday(date)) {
    return 'Today'
  }
  if (isTomorrow(date)) {
    return 'Tomorrow'
  }
  if (isThisWeek(date)) {
    return 'This Week'
  }
  if (isThisMonth(date)) {
    return 'This Month'
  }

  return ''
}

export default function GridArchiveItemAlt({ hit, size }: GridArchiveItemProps) {
  if (!hit.post_title || !hit.permalink) {
    return null
  }

  const imageUrl = hit?.images?.grid_image_url || null
  const imageAlt = hit?.images?.grid_image_alt || null

  const postTitle = decodeSafeEntities(hit.post_title)
  const businessName = decodeSafeEntities(hit.business_info?.name)
  const location = decodeSafeEntities(hit?.location)

  const postDate = hit?.post_date_formatted
  const eventDate = hit?.event_date?.date_formatted

  const eventDateLabel = hit?.event_date?.start_date_unix
    ? new Date(hit.event_date.start_date_unix * 1000)
    : null
  const eventDateInWords = eventDateLabel ? getDateInWords(eventDateLabel) : null

  let sizes = '100vw'

  if (size === 'l') {
    sizes = '(min-width: 1920px) 828px, (min-width: 500px) 43.57vw, calc(95.56vw - 19px)'
  } else {
    sizes =
      '(min-width: 1940px) 544px, (min-width: 1300px) calc(25.65vw + 52px), (min-width: 500px) calc(46.41vw - 14px), calc(95.56vw - 19px)'
  }

  return (
    <article className="flex w-full justify-start gap-s @container/grid-item xs:flex-col xs:gap-0 xs:border-b xs:pb-m xs:last:border-b-0 xs:last:pb-0">
      <div className="flex w-full flex-col gap-s xs:order-2 xs:gap-xs">
        <div className="group relative">
          <Link
            href={hit.permalink}
            className="h4 transition-color s:h5 mb-0 duration-200 ease-in-out group-hover:text-primary">
            {postTitle}
          </Link>
        </div>

        {/* insights & news */}
        {hit.post_type === 'all-work' || hit.post_type === 'all-news' ? (
          <ul className="p-small w-full leading-tight text-grey-800">
            <li className="mr-xxxs inline">{businessName ? businessName : 'Jersey Finance'}</li>

            {postDate ? (
              <li className="inline">
                <span className="mr-xxxs">|</span>
                {postDate}
              </li>
            ) : null}
          </ul>
        ) : null}

        {/* events */}
        {hit.post_type === 'all-events' ? (
          <ul className="p-small w-full leading-tight text-grey-800">
            {eventDate ? <li className="mr-xxxs inline">{eventDate}</li> : null}

            {location ? (
              <li className="inline">
                {location && eventDate ? <span className="mr-xxxs">|</span> : null}
                {location}
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>

      {imageUrl ? (
        <Link
          href={hit.permalink}
          className="relative mb-s w-1/3 shrink-0 overflow-hidden bg-primary-25 duration-500 group-hover:text-primary xs:order-1 xs:w-full xs-up:max-w-[250px]">
          <AppImage
            path={imageUrl}
            alt={imageAlt}
            height={237}
            width={396}
            loading="lazy"
            transforms={{
              width: '396',
              height: '237',
              focus: 'auto',
              dpr: 'auto',
              quality: 'auto',
              trim: '1',
            }}
            className="w w-full object-contain"
            lqip={{
              active: false,
            }}
            sizes={sizes}
            srcSet={{
              396: { width: '396', height: '237' },
              606: { width: '606', height: '363' },
              828: { width: '828', height: '496' },
            }}
          />

          {eventDateInWords ? (
            <PostLabel className="absolute left-0 top-0 z-10 m-xs">{eventDateInWords}</PostLabel>
          ) : null}
        </Link>
      ) : null}
    </article>
  )
}
